import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import { 
  Search, 
  Calendar, 
  Users, 
  MapPin, 
  DollarSign,
  Trophy,
  Eye,
  TrendingUp,
  Filter,
  X,
  ArrowRight,
  Info
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGE_CATEGORIES = ['All', 'U8', 'U10', 'U12', 'U14', 'U16', 'U18', 'Adults'];
const GENDERS = ['All', 'Boys', 'Girls', 'Mixed'];
const STATUSES = ['All', 'upcoming', 'active', 'completed'];
const LEVELS = ['All', 'district', 'regional', 'state', 'national'];

export const SearchTournaments = () => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    age_category: 'All',
    gender: 'All',
    status: 'All',
    level: 'All',
    location: '',
    start_date_from: '',
    start_date_to: '',
    max_entry_fee: '',
  });

  const [resultCount, setResultCount] = useState(0);

  useEffect(() => {
    searchTournaments();
  }, []);

  const searchTournaments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (searchQuery.trim()) params.append('q', searchQuery.trim());
      if (filters.age_category !== 'All') params.append('age_category', filters.age_category);
      if (filters.gender !== 'All') params.append('gender', filters.gender);
      if (filters.status !== 'All') params.append('status', filters.status);
      if (filters.level !== 'All') params.append('level', filters.level);
      if (filters.location.trim()) params.append('location', filters.location.trim());
      if (filters.start_date_from) params.append('start_date_from', filters.start_date_from);
      if (filters.start_date_to) params.append('start_date_to', filters.start_date_to);
      if (filters.max_entry_fee) params.append('max_entry_fee', filters.max_entry_fee);
      
      params.append('limit', '50');

      const response = await axios.get(`${API}/tournaments/search?${params.toString()}`);
      setTournaments(response.data);
      setResultCount(response.data.length);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    searchTournaments();
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const clearFilters = () => {
    setFilters({
      age_category: 'All',
      gender: 'All',
      status: 'All',
      level: 'All',
      location: '',
      start_date_from: '',
      start_date_to: '',
      max_entry_fee: '',
    });
    setSearchQuery('');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'active': return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'completed': return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
      default: return '';
    }
  };

  const getLevelBadgeColor = (level) => {
    switch (level) {
      case 'national': return 'bg-purple-500/10 text-purple-500 border-purple-500/20';
      case 'state': return 'bg-orange-500/10 text-orange-500 border-orange-500/20';
      case 'regional': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'district': return 'bg-green-500/10 text-green-500 border-green-500/20';
      default: return '';
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-black mb-2">
          <span className="text-primary">SEARCH TOURNAMENTS</span>
        </h1>
        <p className="text-muted-foreground">
          Find the perfect tournament for your team
        </p>
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search tournaments by name, location, organizer, venue..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 text-base"
            />
          </div>
          <Button type="submit" className="btn-primary h-12 px-6" disabled={loading}>
            {loading ? 'Searching...' : 'Search'}
          </Button>
          <Button 
            type="button" 
            variant="outline" 
            className="h-12"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="w-5 h-5" />
          </Button>
        </div>
      </form>

      {/* Advanced Filters */}
      {showFilters && (
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Advanced Filters</CardTitle>
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />
                Clear All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Age Category */}
              <div className="space-y-2">
                <Label>Age Category</Label>
                <Select value={filters.age_category} onValueChange={(v) => handleFilterChange('age_category', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AGE_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Gender */}
              <div className="space-y-2">
                <Label>Gender</Label>
                <Select value={filters.gender} onValueChange={(v) => handleFilterChange('gender', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {GENDERS.map(g => (
                      <SelectItem key={g} value={g}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Status */}
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={filters.status} onValueChange={(v) => handleFilterChange('status', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(s => (
                      <SelectItem key={s} value={s}>{s === 'All' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Level */}
              <div className="space-y-2">
                <Label>Level</Label>
                <Select value={filters.level} onValueChange={(v) => handleFilterChange('level', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEVELS.map(l => (
                      <SelectItem key={l} value={l}>{l === 'All' ? 'All' : l.charAt(0).toUpperCase() + l.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Location */}
              <div className="space-y-2">
                <Label>Location</Label>
                <Input
                  placeholder="City, State..."
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                />
              </div>

              {/* Start Date From */}
              <div className="space-y-2">
                <Label>Start Date From</Label>
                <Input
                  type="date"
                  value={filters.start_date_from}
                  onChange={(e) => handleFilterChange('start_date_from', e.target.value)}
                />
              </div>

              {/* Start Date To */}
              <div className="space-y-2">
                <Label>Start Date To</Label>
                <Input
                  type="date"
                  value={filters.start_date_to}
                  onChange={(e) => handleFilterChange('start_date_to', e.target.value)}
                />
              </div>

              {/* Max Entry Fee */}
              <div className="space-y-2">
                <Label>Max Entry Fee ($)</Label>
                <Input
                  type="number"
                  placeholder="100"
                  value={filters.max_entry_fee}
                  onChange={(e) => handleFilterChange('max_entry_fee', e.target.value)}
                />
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <Button onClick={searchTournaments} className="btn-primary" disabled={loading}>
                Apply Filters
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results Count */}
      <div className="mb-6 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {loading ? 'Searching...' : `Found ${resultCount} tournament${resultCount !== 1 ? 's' : ''}`}
        </p>
        {(searchQuery || Object.values(filters).some(v => v && v !== 'All')) && (
          <Button variant="outline" size="sm" onClick={clearFilters}>
            Clear Search
          </Button>
        )}
      </div>

      {/* Results Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : tournaments.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tournaments.map((tournament) => (
            <Card key={tournament.id} className="card-hover overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-primary to-accent" />
              
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between mb-2">
                  <Badge variant="outline" className={getStatusColor(tournament.status)}>
                    {tournament.status.toUpperCase()}
                  </Badge>
                  <Badge variant="outline" className={getLevelBadgeColor(tournament.level)}>
                    {tournament.level.toUpperCase()}
                  </Badge>
                </div>
                <CardTitle className="text-xl font-bold line-clamp-2">{tournament.name}</CardTitle>
                <Badge variant="secondary" className="w-fit mt-1">
                  {tournament.age_category} • {tournament.gender}
                </Badge>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {tournament.description}
                </p>
                
                {/* Key Info */}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-xs">
                      {formatDate(tournament.start_date)} - {formatDate(tournament.end_date)}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-xs truncate">
                      {tournament.location || 'Location TBA'}
                      {tournament.venue_name && ` • ${tournament.venue_name}`}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-xs">
                      {tournament.registered_teams}/{tournament.max_teams} Teams
                      {tournament.registered_teams >= tournament.max_teams && 
                        <Badge variant="destructive" className="ml-2">FULL</Badge>
                      }
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-xs">
                      ${tournament.entry_fee} entry + $10 registration
                    </span>
                  </div>

                  {/* Organizer */}
                  <div className="flex items-center gap-2">
                    <Info className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-xs">By {tournament.organizer_name}</span>
                  </div>
                </div>

                {/* Popularity Metrics */}
                <div className="flex items-center gap-4 pt-2 border-t text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Eye className="w-3 h-3" />
                    {tournament.views || 0}
                  </div>
                  <div className="flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" />
                    {tournament.registered_teams} teams
                  </div>
                </div>
                
                <Link to={`/tournaments/${tournament.id}`}>
                  <Button className="w-full gap-2" variant="outline">
                    View Details
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="text-center py-12">
          <CardContent>
            <Trophy className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-xl font-bold mb-2">No Tournaments Found</h3>
            <p className="text-muted-foreground mb-6">
              {searchQuery || Object.values(filters).some(v => v && v !== 'All')
                ? 'Try adjusting your search criteria'
                : 'No tournaments available at the moment'}
            </p>
            <Button onClick={clearFilters} variant="outline">
              Clear Filters
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
